import React, { useState, useEffect, useRef, useLayoutEffect } from "react";

import { useDispatch, useSelector } from "react-redux";
import {
  switchInteractivePageSection,
  actionGroup,
  deleteWidgets,
  cloneSlider,
  createWidget,
  updateSpatialSettingMode,
  updateFlipbookSettingMode,
  arrangeWidget,
  updateEditorAppearance,
  updateWidgetProperty,
} from "actions/editor";

import _ from "lodash";
import cx from "classnames";
import { Fade } from "react-awesome-reveal";
import { Tabs, Tab, Button } from "react-bootstrap";

import { EditorContext } from "../../index";
import StoryboardContext from "components/Storyboard/context";
import { BackgroundView } from "../background";
import Portal from "components/Portal";
import { MediaLibraryModal } from "components/MediaLibrary";
import { GlassThemesSettings, SlidePageDetailsComponent } from "./components";
import shortid from "shortid";
import { DEFAULT_COLOR, DEFAULT_MEDIA_PROPERTY, POSITION, SCALE } from "constants/background";
import { Font } from "components/Editor/components/properties/fonts";
import Tooltip from "components/Tooltip";

import { getColorsConfig } from "services/cssHelper.service";
import { associateFontsWithWigdet } from "actions/fonts";
import { Property } from "../../components/properties";
import { SLIDE } from "constants/widgetTypes";
import { Widget } from "../core";
import { useEmptyWidgetSelection } from "components/Editor/hooks/useEditorSession";

import styles from "./slideRender.module.scss";


export const SlidesContext = React.createContext({
  view: "page",
  cardSettings: false,
});

const SlideRenderComponent = ({ entityType, locked }) => {
  const slideRef = useRef(null);
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;
  const editorWidgetViewer = document.getElementsByClassName(
    "editor_widget_viewer"
  )[0];
  const breakpoint = useSelector((state) =>
    _.get(state, `${editor}.present.breakpoint`)
  );

  const storyboard = React.useContext(StoryboardContext);
  const storyboardType = _.get(storyboard, "type");
  const background = useSelector((state) =>
    _.get(state, `${editor}.present.appearance.background`)
  );
  const slideBackground = useSelector((state) =>
    _.get(state, `${editor}.present.appearance.slideBackground`)
  );
  const widgets = useSelector((state) =>
    _.get(state, `${editor}.present.widgets`, [])
  );

  const slideWidgets = useSelector((state) => {
    return widgets.filter(
      (item) => item.type === SLIDE && _.get(item, "properties.desktop.visible")
    );
  });

  const showToolbars = useEmptyWidgetSelection(editor);

  const spatialSettingMode = useSelector((state) =>
    _.get(state, `${editor}.present.spatialSettingMode`, null)
  );

  const flipbookSettingMode = useSelector((state) =>
    _.get(state, `${editor}.present.flipbookSettingMode`, null)
  );

  const pageEntityType = useSelector((state) =>
    _.get(state, `${editor}.present.entityType`)
  );

  const activePageSection = useSelector((state) =>
    _.get(state, `${editor}.present.interactive.active`)
  );

  const activeSlideWidget = widgets.find((w) => w.uid === activePageSection);
  const flipbookSlideBackground = _.get(activeSlideWidget, 'properties.desktop.background', null);

  const [slideContext, setSlideContext] = useState({
    view: "page",
    cardSettings: false,
  });

  const handleClickOutside = (event) => {
    if (slideRef && !slideRef.current.contains(event.target)) {
      setSlideContext((state) => {
        return {
          ...state,
          view: "page",
          cardSettings: false,
        };
      });
      return;
    }
  };

  useLayoutEffect(() => {
    const activeSection = document.getElementById(activePageSection);

    if (activeSection) {
      activeSection.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [activePageSection]);

  useEffect(() => {
    if (editorWidgetViewer) {
      editorWidgetViewer.addEventListener("mousedown", handleClickOutside);

      return () => {
        editorWidgetViewer.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [editorWidgetViewer]);

  const handleLayoutSettings = (e) => {
    if (!showToolbars || !e.target.id || spatialSettingMode || flipbookSettingMode) {
      return;
    }
    setSlideContext((state) => {
      return {
        view: "card",
        cardSettings: !state.cardSettings,
      };
    });
  };

  const handleViewChange = (view) => {
    if (!view) {
      return;
    }
    setSlideContext((state) => {
      return {
        view: "page",
        cardSettings: false,
      };
    });
    if (storyboardType === "SPATIAL") {
      dispatch(updateSpatialSettingMode(editor, view));
    } else {
      dispatch(updateFlipbookSettingMode(editor, view));
    }
  };

  const handleCloseSettings = () => {
    if (storyboardType === "SPATIAL") {
      dispatch(updateSpatialSettingMode(editor, null));
    } else {
      dispatch(updateFlipbookSettingMode(editor, null));
    }
  };

  return (
    <SlidesContext.Provider value={slideContext}>
      {background && editorWidgetViewer &&  storyboardType !== 'FLIP_BOOK' &&(
        <Portal mountNode={editorWidgetViewer}>
          <BackgroundView
            className={styles.pageBackground}
            background={background}
          />
        </Portal>
      )}
      {_.size(slideWidgets) > 0 && (
        <div
          ref={slideRef}
          className={cx(
            styles.slideWrapper,
            "slideRender_Wrapper",
            { [styles.hide]: (spatialSettingMode == "highlights" || flipbookSettingMode == 'highlights') },
            {
              [styles.pageDetailView]: ((spatialSettingMode === "pageDetails" || flipbookSettingMode === "pageDetails") && breakpoint == 'desktop'),
            }
          )}
        >
          { (storyboardType === 'FLIP_BOOK' ? flipbookSlideBackground : slideBackground) &&
            <BackgroundView
              className={styles.backgroundView}
              background={storyboardType === 'FLIP_BOOK' ? flipbookSlideBackground : slideBackground}
            />
          }
          <div onClick={handleLayoutSettings} className={styles.container}>
            {_.map(_.orderBy(slideWidgets, "order", "asc"), (widget, index) => {

              if(pageEntityType !== 'GATING' && widget.uid !== activePageSection) {
                return null;
              }

              return (
                <Widget
                  key={widget.uid}
                  widgetProps={widget}
                  entityType={entityType}
                />
              );
            })}
          </div>

          <Portal mountNode={editorWidgetViewer}>
            {(showToolbars && breakpoint == 'desktop') && !locked && pageEntityType !== 'GATING' &&
              <SlideWidgetDesktopAction
                editor={editor}
                storyboardType={storyboardType}
                view={storyboardType === 'SPATIAL' ? spatialSettingMode : flipbookSettingMode}
                onChangeView={handleViewChange}
                onClose={handleCloseSettings}
                breakpoint={breakpoint}
              />
            }
          </Portal>

          {(showToolbars && !locked ) && pageEntityType !== 'GATING' && (
              <SlideControllerAction />
          )}
          { breakpoint === 'desktop' && 
            <SliderBottomMenu
              spatialSettingMode={storyboardType === 'SPATIAL' ? spatialSettingMode : flipbookSettingMode}
              editorSession={editor}
              breakpoint={breakpoint}
            />
          }
          {((!spatialSettingMode || !flipbookSettingMode) && !locked) && pageEntityType !== 'GATING' &&
            <AddNewSlide
              editor={editor}
              slideWidgets={slideWidgets}
              activePageSection={_.find(slideWidgets, {
                uid: activePageSection,
              })}
            />
          }
        </div>
      )}
      <Portal mountNode={editorWidgetViewer}>
        {/* <div className={cx(styles.highlightsContainer, {[styles.showHighlight]: spatialSettingMode == 'highlights'})}>
          <div className={styles.header}>

          </div>
          <div className={styles.statsContainer}></div>
        </div> */}
        <SlidePageDetailsComponent
          show={spatialSettingMode == 'pageDetails' || flipbookSettingMode == 'pageDetails'}
          onClose={handleCloseSettings}
        />
      </Portal>
    </SlidesContext.Provider>
  );
};

const SliderBottomMenu = ({ spatialSettingMode, editorSession, breakpoint }) => {

  const pages = useSelector(state => _.get(state, 'pages.items', []));
  const menuDesigns =  useSelector((state) =>_.get(state, `${editorSession}.present.appearance.menuDesign`));

  const [state, setState] = useState({
    buttonStyle: {},
    containerStyle: {}
  });

  useEffect(() => {
    var menuButtonStyles = getStyles(menuDesigns);
    var wrapperStyle = getWrapperStyles(menuDesigns);

    setState(state => {
      return {
        buttonStyle: menuButtonStyles,
        containerStyle: wrapperStyle
      }
    })
  }, [menuDesigns])

  const LeftArrow = () => {
    return (
      <div className={styles.menuIcon}>
        <i style={{color: state.containerStyle.color}} className={cx(styles.icon, "cleverstory-drop-down-left")} />
      </div>
    );
  };

  const RightArrow = () => {
    return (
      <div className={styles.menuIcon}>
        <i style={{color: state.buttonStyle.color}} className={cx(styles.icon, "cleverstory-drop-down-right")} />
      </div>
    );
  };

  const MenuItem = ({ title, itemId, page }) => {
    return (
      <div
        role="menuitem"
        id={page.id}
        tabIndex={0}
        style={state.buttonStyle}
        key={itemId}
        className={cx(styles.menuItemButton)}
      >
        <h3 className={styles.menuItemTitle}>{title}</h3>
      </div>
    );
  };

  return (
    <div
      className={cx(styles.menuDesignConatiner, {
        [styles.showMenuDesigns]: (spatialSettingMode == "menuDesign"),
        [styles.mobileMenuContainer]: breakpoint == 'mobile'
      })}
    >
      <div style={state.containerStyle} className={styles.mainMenu}>
        {breakpoint == "desktop" && (
          <div style={{ maxWidth: "700px", overflow: "hidden", display:"flex"}}>
            <LeftArrow/>
            <div className={styles.scrollMenu}>
              {pages &&
                pages.map((page, index) => (
                  <MenuItem
                    itemId={index}
                    key={index}
                    page={page}
                    title={page.name}
                  />
              ))}
            </div>
            <RightArrow/>
          </div>
        )}
        {breakpoint !== "desktop" && (
          <React.Fragment>
            <LeftArrow />
            <RightArrow />
          </React.Fragment>
        )}
      </div>
      <div style={state.containerStyle} className={styles.mainMenu}>
        {breakpoint == 'desktop' &&
          <div className={styles.menuIcon}>
            <i style={{color: state.containerStyle.color}} className={cx(styles.icon, 'cleverstory-grid')} />
          </div>
        }
        {breakpoint == 'desktop' &&
          <div className={styles.menuIcon}>
            <i style={{color: state.containerStyle.color}} className={cx(styles.icon, 'cleverstory-expand-editor')} />
          </div>
        }
        {breakpoint !== 'desktop'&&
          <div className={styles.menuIcon}>
            <span style={{color: state.containerStyle.color}} className={cx(styles.icon, 'cleverstory-sptial-home')}>
              <span className="path1"></span>
              <span className="path2"></span>
            </span>
          </div>
        }
        {/* {breakpoint === 'desktop' &&
          <div className={styles.menuIcon} onClick={onToggleHighlight}>
            <i className={cx(styles.icon, 'cleverstory-settings')} />
          </div>
        } */}
        {/* <div className={styles.menuIcon}>
          <i className={cx(styles.icon, 'cleverstory-more')} />
        </div> */}
      </div>
    </div>
  );
};

const AddNewSlide = ({ editor, slideWidgets, activePageSection }) => {
  const dispatch = useDispatch();

  const widgetsList = useSelector((state) =>
    _.get(state, `${editor}.present.widgets`, [])
  );

  const _cleanData = (widget) => {
    const children = widget.widgets;
    if (_.size(children) > 0) {
      widget.widgets = children.map((child) => _cleanData(child));
    }

    return _.omit(widget, ["id", "uid", "parent", "template", "icon"]);
  };

  const handleCreateNewSlide = () => {
    var cloned = _.cloneDeep(activePageSection);
    cloned = _cleanData(cloned);
    var uid = `widget_${shortid.generate()}`,
      cloned = {
        ...cloned,
        name: `Slide ${_.size(slideWidgets) + 1}`,
        uid: uid,
      };

    var widgets = widgetsList;
    widgets = widgets.filter((item) => item.type === SLIDE);
    widgets = _.orderBy(widgets, "order", "desc");

    const index = widgets.findIndex(
      (item) => item.uid === activePageSection.uid
    );

    actionGroup.start();
    dispatch(createWidget(editor, [cloned], null, false));
    dispatch(arrangeWidget(editor, cloned, index));
    dispatch(switchInteractivePageSection(editor, cloned.uid));
    actionGroup.end();
  };

  return (
    <div className={styles.bottomContainer}>
      <Button
        variant="primary"
        onClick={handleCreateNewSlide}
        className={styles.addNewSlide}
      >
        <i className={cx("cleverstory-plus")}></i>
        Add Slide
      </Button>
    </div>
  );
};

const slideRenderEquals = function (prevProps, nextProps) {
  const paths = ["className", "style", "widgetProps", "widgetRef", "children", "locked"];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
};

const SlideRender = React.memo(SlideRenderComponent, slideRenderEquals);

const SlideWidgetDesktopAction = ({
  editor,
  storyboardType,
  view,
  onChangeView,
  onClose,
  breakpoint,
}) => {
  const activePageSection = useSelector((state) =>
    _.get(state, `${editor}.present.interactive.active`)
  );
  const background = useSelector((state) =>
    _.get(state, `${editor}.present.appearance.background`)
  );

  const slideBackground = useSelector((state) => {
    return _.get(
      state,
      `${editor}.present.appearance.slideBackground`,
      {}
    );
  });

  const widgets = useSelector((state) =>
    _.get(state, `${editor}.present.widgets`, [])
  );
  const activeSlideWidget = widgets.find((w) => w.uid === activePageSection);
  const flipbookSlideBackground = _.get(activeSlideWidget, 'properties.desktop.background', null);

  useEffect(()=>{
    return ()=> {
      onClose();
    }
  },[])

  const changeView = (view) => {
    onChangeView(view);
  };

  return (
    <div id={`slide_action`} className={styles.widgetAction}>
      <Fade>
        <div className={cx(styles.textButton, styles.propertyAction)}>
          {storyboardType !== 'FLIP_BOOK' &&
              <>
                <Property.Action
                  className={styles.iconButton}
                  onClick={() => changeView("ambiance")}
                  tooltip='Ambiance'
                >
                  <i className={cx("cleverstory-ambiance")} />
                </Property.Action>
              
              <Property.Divider />
            </>
          }
          <Property.Action
            className={styles.iconButton}
            onClick={() => changeView("menuDesign")}
            tooltip='Menu Design'

          >
            <i className={cx("cleverstory-slide-action-menu")} />
          </Property.Action>
          <Property.Divider />
          <Property.Action
            className={styles.iconButton}
            onClick={() => changeView("slideBackground")}
            tooltip='Slide Background'
          >
            <i className={cx("cleverstory-slide-background")} />
          </Property.Action>
        </div>
      </Fade>
      {view === "ambiance" && (
        <AmbianceDialog
          id={`slide_action`}
          background={background}
          onCancel={() => {
            onClose();
          }}
          view={view}
        />
      )}
      {view === "menuDesign" && (
        <MenuDesignDialog
          id={activePageSection}
          onCancel={() => {
            onClose();
          }}
        />
      )}
      { view === 'slideBackground' &&
          <SlideBackground
            id={activePageSection}
            editorSession={editor}
            background={storyboardType === 'FLIP_BOOK' ? flipbookSlideBackground : slideBackground}
            storyboardType={storyboardType}
            widgetId={activePageSection}
            onCancel={() => {
              onClose();
            }}
            view={view}
          />
      }
    </div>
  );
};

const SlideControllerAction = () => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;
  const activePageSection = useSelector((state) =>
    _.get(state, `${editor}.present.interactive.active`)
  );
  const entityType = useSelector((state) =>
    _.get(state, `${editor}.present.entityType`)
  );
  const widgets = useSelector((state) =>
    _.get(state, `${editor}.present.widgets`, [])
  );

  const slideWidgets = useSelector((state) => {
    const sliders = widgets.filter(
      (item) => item.type === SLIDE && _.get(item, "properties.desktop.visible")
    );

    return _.orderBy(sliders, "order", "asc");
  });
  const currentIndex = slideWidgets.findIndex(
    (item) => item.uid === activePageSection
  );

  const disablePrevious = _.size(slideWidgets) <= 1 || currentIndex === 0;
  const disableNext = _.size(slideWidgets) <= 1 || currentIndex === _.size(slideWidgets) - 1;

  const handleNext = () => {
    if (currentIndex == _.size(slideWidgets) - 1) {
      return;
    }

    const nextSlide = slideWidgets[currentIndex + 1];
    dispatch(switchInteractivePageSection(editor, nextSlide.uid));
  };

  const handlePrev = () => {
    if (currentIndex === 0) {
      return;
    }

    const nextSlide = slideWidgets[currentIndex - 1];
    dispatch(switchInteractivePageSection(editor, nextSlide.uid));
  };

  const onDelete = () => {
    const deleteWidget = _.find(slideWidgets, { uid: activePageSection });
    actionGroup.start();
    dispatch(deleteWidgets(editor, [deleteWidget.uid]));
    if (currentIndex === _.size(slideWidgets) - 1) {
      handlePrev();
      return;
    }
    handleNext();
    actionGroup.end();
  };

  const cloneSlide = () => {
    dispatch(cloneSlider(editor, activePageSection));
  };

  return (
    <div className={styles.sliderControl}>
      <Fade fraction={0}>
        <div className={styles.propertyAction}>
          <Property.Action
            onClick={handlePrev}
            disabled={disablePrevious}
            className={cx(styles.iconButton, styles.previous, disablePrevious && styles.disabledArrow)}
            tooltip='Previous'
          >
            <i className={cx("cleverstory-back")} />
          </Property.Action>
          <Property.Action
            onClick={handleNext}
            disabled={disableNext}
            className={cx(styles.iconButton, styles.next, disableNext && styles.disabledArrow)}
            tooltip='Next'
          >
            <i className={cx("cleverstory-back")} />
          </Property.Action>
          <Property.Divider className={styles.divider} />
          <Property.Action onClick={cloneSlide} className={styles.iconButton} tooltip='Clone'>
            <i className={cx("cleverstory-clone")} />
          </Property.Action>
          <Property.Divider className={styles.divider} />
          {_.size(slideWidgets) > 1 && (
            <Property.Action onClick={onDelete} className={styles.iconButton} tooltip='Delete'>
              <i className={cx("cleverstory-trash")} />
            </Property.Action>
          )}
        </div>
      </Fade>
    </div>
  );
};


const AmbianceDialog = ({
  id,
  widget,
  position,
  onChange,
  background,
  view,
  ...rest
}) => {
  const dispatch = useDispatch();
  const [backgroundView, setBackgroundView] = useState(null);
  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const storyboardContext = React.useContext(StoryboardContext);
  const type = _.get(background, "type", "");
  const color =
    type === "gradient" || type === "solid"
      ? background
      : _.get(background, "color") || DEFAULT_COLOR;

  const handleBackgroundChange = (value) => {
    const request = {
      background: value,
    };
    
    dispatch(updateEditorAppearance(editorSession, request));
    closePopup();
  };

  const handleColorChange = (color) => {
    var request = {
      type: "color",
      color: color,
    };

    dispatch(updateEditorAppearance(editorSession, { background: request }));
  };

  const handleImageChange = (media) => {
    var existing = background || DEFAULT_MEDIA_PROPERTY;

    const type = _.get(existing, "type");
    if (type != "image") {
      existing = DEFAULT_MEDIA_PROPERTY;
    }

    var change = {
      ...existing,
      type: "image",
      media: media[0],
    };

    dispatch(updateEditorAppearance(editorSession, { background: change }));
    closePopup();
  };

  const handleVideoChange = (media) => {
    var existing = background || DEFAULT_MEDIA_PROPERTY;

    const type = _.get(existing, "type");
    if (type != "video") {
      existing = DEFAULT_MEDIA_PROPERTY;
    }

    var change = {
      ...existing,
      type: "video",
      media: media[0],
    };

    dispatch(updateEditorAppearance(editorSession, { background: change }));
    closePopup();
  };

  const closePopup = () => {
    setBackgroundView(null);
  };

  return (
    <>
      <Property.Dialog
        className={styles.ambianceDialogDialog}
        width={246}
        target={`#${id}`}
        title={"Ambiance"}
        onCancel={rest.onCancel}
      >
        <Property.Body>
          <Property
            name="Use a wide Image for better experience"
            className={styles.backgroundViewSettings}
          >
            <div className={styles.viewContainer}>
              <div className={styles.imageBackground}>
                <BackgroundView background={background} />
                <div className={styles.actionGroup}>
                  <Tooltip placement="top" content="Color">
                    <Button
                      className={styles.button}
                      onClick={() => setBackgroundView("color")}
                      variant="info"
                    >
                      <i className={cx("cleverstory-font-color")} />
                    </Button>
                  </Tooltip>
                  <Tooltip placement="top" content="Image">
                    <Button
                      className={styles.button}
                      onClick={() => setBackgroundView("image")}
                      variant="info"
                    >
                      <i className={cx("cleverstory-background")} />
                    </Button>
                  </Tooltip>
                  <Tooltip placement="top" content="Video">
                    <Button
                      className={styles.button}
                      onClick={() => setBackgroundView("video")}
                      variant="info"
                    >
                      <i className={cx("cleverstory-Video-S")} />
                    </Button>
                  </Tooltip>
                </div>
              </div>
            </div>
          </Property>
          {(type === 'image' || type === 'video') &&
          <MediaBackgroundProperty value={background} onChange={handleBackgroundChange} view={view} />
          }
        </Property.Body>
      </Property.Dialog>
      {backgroundView === "color" && (
        <Property.ColorPickerDialog
          target={`#${id}`}
          colorConfig={color}
          value={color}
          gradient={true}
          onChange={handleColorChange}
          onCancel={closePopup}
        />
      )}

      {backgroundView === "image" && (
        <MediaLibraryModal
          show={backgroundView === "image"}
          storyboard={storyboardContext}
          mediaTypes={["image"]}
          multiple={false}
          onSelect={handleImageChange}
          onClose={() => {
            closePopup();
          }}
          onChange={onChange}
        />
      )}

      {backgroundView === "video" && (
        <MediaLibraryModal
          show={backgroundView === "video"}
          storyboard={storyboardContext}
          mediaTypes={["video"]}
          multiple={false}
          onSelect={handleVideoChange}
          onClose={() => {
            closePopup();
          }}
        />
      )}
    </>
  );
};


const SlideBackground = ({ id, editorSession, background, onCancel, view, storyboardType, widgetId }) => {
  const dispatch = useDispatch();

  const storyboardContext = React.useContext(StoryboardContext);
  const type = _.get(background, 'type', "");

  const [state, setState] = useState({
    backgroundOption: null,
    custom: false
  });

  useEffect(()=>{
    
    return ()=> {
      setState((state)=> {
        return {
          backgroundOption: null,
          custom: false
        }
      })
    }
  },[])


  const handleViewChange =(key, view)=> {
    setState((state)=>{
      return {
        ...state,
        [key]: view
      }
    })
  }

  const handleBackgroundChange = (value) => {
    if (storyboardType === 'FLIP_BOOK') {
      dispatch(updateWidgetProperty(editorSession, widgetId, "desktop", { background: value }));
    } else {
      dispatch(updateEditorAppearance(editorSession, { slideBackground: value }));
    }
  };

  const handleCustomBlur = (data) => {
    const blurRequest = {
      slideBackground: {
        ...background,
        blur: data
      },
    };
    dispatch(updateEditorAppearance(editorSession, blurRequest));
  };


  const handleImageChange = (media) => {
    let existing = background || DEFAULT_MEDIA_PROPERTY;
    const type = _.get(existing, 'type');
    if (type != 'image') {
      existing = DEFAULT_MEDIA_PROPERTY;
    }

    const change = {
      ...DEFAULT_MEDIA_PROPERTY,
      ..._.omit(existing, ['blur', 'type', 'media']),
      opacity: _.get(existing, 'opacity', 100),
      color: _.get(existing, 'color', DEFAULT_MEDIA_PROPERTY.color),
      scale: _.get(existing, 'scale', DEFAULT_MEDIA_PROPERTY.scale),
      position: _.get(existing, 'position', DEFAULT_MEDIA_PROPERTY.position),
      type: 'image',
      media: _.get(media, '[0]', null),
    };

    handleBackgroundChange(change)
    closePopup();
  }

  const handleVideoChange = (media) => {
    let existing = background || DEFAULT_MEDIA_PROPERTY;
    const type = _.get(existing, 'type');
    if (type != 'video') {
      existing = DEFAULT_MEDIA_PROPERTY;
    }

    const change = {
      ...DEFAULT_MEDIA_PROPERTY,
      ..._.omit(existing, ['blur', 'type', 'media']),
      opacity: _.get(existing, 'opacity', 100),
      color: _.get(existing, 'color', DEFAULT_MEDIA_PROPERTY.color),
      scale: _.get(existing, 'scale', DEFAULT_MEDIA_PROPERTY.scale),
      position: _.get(existing, 'position', DEFAULT_MEDIA_PROPERTY.position),
      type: 'video',
      media: _.get(media, '[0]', null),
    };

    handleBackgroundChange(change)
    closePopup();
  }

  const closePopup = () => {
    setState((state)=>{
      return {
        ...state,
        backgroundOption: null
      }
    })
  }

  return (
    <>
    <Property.Dialog
      className={styles.slideBackgroundDialog}
      width={246}
      target={`#${id}`}
      title={"Slide Background"}
      onCancel={onCancel}
    >
      <Property.Body>
        <Property name="Background Fill">
          <div className={styles.actionConatiner}>
            <Tooltip placement="top" content="Color">            
              <Button 
                variant="info" 
                active={
                  background &&
                  (background.type == 'glass' ||
                    (storyboardType === 'FLIP_BOOK' && background.type == 'color'))
                }
                onClick={() => handleViewChange('backgroundOption', 'color')}
              >
                <i className={cx("cleverstory-font-color")} />
              </Button>
            </Tooltip>
            <Tooltip placement="top" content="Image"> 
              <Button 
                variant="info" 
                active={background && background.type == 'image'}
                onClick={() => handleViewChange('backgroundOption', "image")}
              >
                <i className={cx("cleverstory-background")} />
              </Button>
            </Tooltip>
            <Tooltip placement="top" content="Video"> 
              <Button 
                variant="info" 
                active={background && background.type == 'video'}
                onClick={() => handleViewChange('backgroundOption', "video")}
              >
                <i className={cx("cleverstory-Video-S")} />
              </Button>
            </Tooltip>
            <Tooltip placement="top" content="None"> 
              <Button 
                variant="info" 
                active={background == null}
                onClick={() => handleBackgroundChange( null)}
              >
                <i className={cx("cleverstory-none")} />
              </Button>
            </Tooltip>
          </div>
        </Property>
        {  state.backgroundOption === 'color' &&
          <GlassThemesSettings
            background={background}
            handleBackgroundChange={handleBackgroundChange}
            handleCustomBlur={storyboardType !== 'FLIP_BOOK' ? handleCustomBlur : undefined}
            isFlipBook={storyboardType === 'FLIP_BOOK'}
          />
        }

        {(type === 'image' || type === 'video') &&
          <MediaBackgroundProperty value={background} onChange={handleBackgroundChange} view={view}/>
        }

      </Property.Body>
    </Property.Dialog>
    {state.backgroundOption === 'image' &&
      <MediaLibraryModal
        show={state.backgroundOption === 'image'}
        storyboard={storyboardContext}
        mediaTypes={['image']}
        multiple={false}
        onSelect={handleImageChange}
        onClose={() => { closePopup() }}
      />
    }

    {state.backgroundOption === 'video' &&
      <MediaLibraryModal
        show={state.backgroundOption === 'video'}
        storyboard={storyboardContext}
        mediaTypes={['video']}
        multiple={false}
        onSelect={handleVideoChange}
        onClose={() => {closePopup()}}
      />
    }
    </>
  );
};

const MenuDesignDialog = ({ id, widget, position, onChange, view, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const menuDesign = useSelector((state) => {return _.get(state,`${editorSession}.present.appearance.menuDesign`)});
  const fontFamily = _.get(menuDesign, 'regular.font.fontFamily') ? _.get(menuDesign, 'regular.font.fontFamily') : 'Montserrat,sans-serif';

  const handleMenuDesignPropertyChange = (key, value) => {
    const request = {
      menuDesign: {
        ...menuDesign,
        [key]: value
      }
    };

    dispatch(updateEditorAppearance(editorSession, request));
  };

  const handleFontFamily = (value) => {
    const regularStyle = _.get(menuDesign, 'regular');

    var font = _.get(value, 'font.id');
    var family = _.get(value, 'font.fontFamily');
    if (!family) {
      return;
    }

    var fontRequest = {
      ...regularStyle,
      font: {
        ...regularStyle.font,
        fontFamily: family
      }
    }

    handleMenuDesignPropertyChange('regular', fontRequest);
    if (font) {
      dispatch(associateFontsWithWigdet(editorSession, id, [font]));
    }
  }

  const handleTextColorChange = React.useCallback((color) => {
    const fontStyle = _.get(menuDesign, `regular.font`);


    const fontColor = {
        ...menuDesign['regular'],
        font: {
          ...fontStyle,
          color: color
        }
    }

    handleMenuDesignPropertyChange('regular', fontColor);

  }, [menuDesign]);

  const handleBackgroundColorChange = React.useCallback((color) => {
    const fontBackground = {
      ...menuDesign['hover'],
      background: color
    }

    handleMenuDesignPropertyChange('hover', fontBackground);
  }, [menuDesign]);

  const handleCustomBlur =(data)=> {
    const menuBackground = _.get(menuDesign, 'background');
    const blurRequest = {
        ...menuBackground,
        blur: data
    };

    handleMenuDesignPropertyChange('background', blurRequest);
  }

  return (
    <Property.Dialog
      className={styles.ambianceDialogDialog}
      width={256}
      target={`#${id}`}
      title={"Menu Design"}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <GlassThemesSettings 
          background={menuDesign? menuDesign.background: null}
          handleBackgroundChange={(data)=>{handleMenuDesignPropertyChange('background', data)}}
          handleCustomBlur={handleCustomBlur}
        />
        <Font.Family 
          value={fontFamily}
          onChange={handleFontFamily}
        />
        <Property name="Button Design" className={styles.buttonDesignProperty}>
          <Property.Color 
            title={"Font & Icon Color"} 
            value={_.get(menuDesign, 'regular.font.color', null)} 
            onChange={handleTextColorChange}
          />
          <Property.Color 
            title={"Background Hover Color"} 
            gradient={true}
            clearable={true} 
            value={_.get(menuDesign, 'hover.background', null)} 
            onChange={handleBackgroundColorChange}
          />
        </Property>
      </Property.Body>
    </Property.Dialog>
  );
};

const MediaBackgroundProperty = ({ value, onChange, view }) => {
  const type = _.get(value, 'type');
  const opacity = _.get(value, 'opacity', 1);
  const color = _.get(value, 'color') || DEFAULT_MEDIA_PROPERTY;
  const scale = _.get(value, 'scale', 'scale_to_fit');
  const position = _.get(value, 'position', 'center');

  const typeLabel = type === 'image' ? 'Image' : 'Video';

  const handleOpacityChange = (opacity) => {
    const change = {
      ...value,
      opacity: opacity
    };

    onChange(change);
  }

  const handleColorChange = (color) => {
    const change = {
      ...value,
      color: color
    };

    onChange(change);
  }

  const handleScaleChange = (scale) => {
    const change = {
      ...value,
      scale: scale
    };

    onChange(change);
  }

  const handlePositionChange = (position) => {
    const change = {
      ...value,
      position: position
    };

    onChange(change);
  }

  return (
    <>
      <Property name={`${typeLabel} Opacity`}>
        <Property.Slider className={styles.opacityProperty}
          min={1}
          max={100}
          input
          value={opacity}
          onChange={handleOpacityChange}
        />
      </Property>
      <Property.Divider />
      <Property.Color title={`Color Behind ${typeLabel}`} value={color} onChange={handleColorChange} />
      { view !== "ambiance" &&
        <div className={styles.scalingPositionProperty}>
          {type === 'image' &&
            <div className={styles.scalingContainer}>
              <Property name="Scaling" className={styles.scalingProperty}>
                <div className={styles.body}>
                  <Property.RadioButton className={styles.radio}
                    title='Scale to fit'
                    value={scale == SCALE.SCALE_TO_FILL}
                    onClick={() => handleScaleChange(SCALE.SCALE_TO_FILL)}
                  />
                  <Property.RadioButton className={styles.radio}
                    title='Tiled'
                    value={scale == SCALE.TILED}
                    onClick={() => handleScaleChange(SCALE.TILED)}
                  />
                </div>
              </Property>
            </div>
          }
          <div className={styles.positionContainer}>
            <Property name="Position" className={styles.positionProperty}>
              <div className={styles.body}>
                {Object.keys(POSITION).map(key => {
                  const value = POSITION[key];
                  const active = position === value;

                  return (
                    <button className={cx({
                      [styles.active]: active
                    })} onClick={() => handlePositionChange(value)}
                    >
                      <i className={cx(value === POSITION.CENTER ? "cleverstory-plus" : "cleverstory-back")} />
                    </button>
                  );
                })}
              </div>
            </Property>
          </div>
        </div>
      }
    </>
  )
}



/**
 * Generate Styles
 * 
*/
function getWrapperStyles(properties) {
  var styles = {};

  if (!properties) {
    return styles;
  }


  //font-color
  const colorRgb = _.get(properties, `regular.font.color`, null);
  const color = getColorStyles(colorRgb);
  styles = {
    ...styles,
    ...color
  }


  //background
  const backgroundRgb = _.get(properties, `background`, null);
  var background = 'inherit';
  if (backgroundRgb) {
    background = getColorsConfig(backgroundRgb);
  }

  styles['background'] = background;


  //blur
  const blur = _.get(properties, `background.blur`, null);
  var blurEffect = 20;
  if (blur) {
    blurEffect = blur;
  }

  styles['backdrop-filter'] = `blur(${blurEffect}px)`;

  return styles;
}


/**
 * Generate Styles
 * 
*/
function getStyles(properties) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const defaultStyles = _.get(properties, `regular`, {});

  //font-color
  const colorRgb = _.get(defaultStyles, `font.color`, null);
  const color = getColorStyles({...colorRgb, a: 0.60});
  styles = {
    ...styles,
    ...color
  }

  const font = _.get(defaultStyles, 'font', {});

  //font-family
  const fontFamily = _.get(font, 'fontFamily');
  if (fontFamily) {
    styles = {
      ...styles,
      fontFamily: fontFamily
    }
  }

  return styles;
}

export function getColorStyles(value) {
  if (!value) {
    return null;
  }

  const colorRgb = value;

  var color = 'inherit';
  if (colorRgb) {
    color = `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
  }

  var styles = {
    color: color
  };

  return styles;
}

export { SlideRender };
